import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { SVGAssets } from '@business/assets/vectors';
import { useEffect, useMemo, useState } from 'react';
import { FamilyFormProps } from '@business/interfaces/familyForm/familyForm';
import { useDispatch, useSelector } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import { relativeFormSchema } from '@utils/validations/relative-form';
import {
  useAddPeopleMutation,
  useGetPeopleByIdQuery,
  useGetPeopleByNameQuery,
  useGetRelationshipTypesQuery,
  useUpdatePeopleMutation
} from '@ports/internal-services/people/peopleSlice';
import { setAlertDetails } from '@ports/common/AlertStateSlice';
import { getUserData } from '@ports/common/userStateSlice';
import { uploadToS3 } from '@utils/helpers/s3Upload';
import { getToken } from '@ports/common/sessionStateSlice';
import { AlertProps } from '@business/interfaces/alert';
import { Input } from '../Input';
import { Button } from '../Button';
import { Select } from '../Select';
import { SearchableSelectInput } from '../SearchableInput';
import { Alert } from '../Alert';
import { UploadPhotoModal } from '../MyProfileForm';

export type RelativeFormData = {
  name: string;
  relationshipType: string;
  nickName: string;
  gender: string;
  birthday: string;
  birthPlace: string;
  familyMemberName: string;
  familyMemberRelation: string;
  diary: string;
  isPrivate: boolean;
  dadName: string;
  momName: string;
  dadId: string;
  momId: string;
  email: string;
};

export type RelativeMember = {
  id: string;
  name: string;
  relation: string;
};

export const RelativeForm = ({
  isOpen,
  onClose,
  name,
  peopleFiltersHandler,
  personId,
  setIsLoading
}: FamilyFormProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const userDetails = useSelector(getUserData);
  const token = useSelector(getToken);
  const [isPrivate, setIsPrivate] = useState(false);
  const [showFamilyMember, setShowFamilyMember] = useState<boolean>(false);
  const [familyMembers, setFamilyMembers] = useState<RelativeMember[]>([]);
  const [currentFamilyMemberInput, setCurrentFamilyMemberInput] = useState<{
    id: string;
    name: string;
    relation: string;
  }>({ id: '', name: '', relation: '' });
  const [editingMember, setEditingMember] = useState<{
    index: number;
    id: string;
    name: string;
    relation: string;
  } | null>(null);
  const [searchText, setSearchText] = useState<string>('');
  const [isNameSelected, setIsNameSelected] = useState<boolean>(false);
  const [selectedPersonId, setSelectedPersonId] = useState<string>('');
  const [hasNameInteraction, setHasNameInteraction] = useState<boolean>(false);
  const [profilePictureFile, setProfilePictureFile] = useState<File | null>(
    null
  );
  const [profilePicturePreview, setProfilePicturePreview] =
    useState<string>('');
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [alertData, setAlertData] = useState<AlertProps | null>(null);
  const [showUploadModal, setShowUploadModal] = useState<boolean>(false);

  const [AddFamily] = useAddPeopleMutation();
  const [UpdatePeople] = useUpdatePeopleMutation();
  const { data: peopleDataForSearch } = useGetPeopleByNameQuery(
    {
      search: searchText
    },
    { skip: !searchText }
  );

  const { data: relationshipTypeOptions } = useGetRelationshipTypesQuery({});

  const relationshipOptions = useMemo(() => {
    return (
      relationshipTypeOptions?.data?.rows?.map((row: any) => ({
        value: row.id,
        label: row.name
      })) || []
    );
  }, [relationshipTypeOptions?.data?.rows]);

  const familySearchOptions = useMemo(() => {
    return (
      peopleDataForSearch?.data?.rows?.map((item: any) => ({
        id: item?.id,
        name: item?.name,
        profilePicUrl: item?.profileImage
      })) || []
    );
  }, [peopleDataForSearch?.data?.rows]);

  // Filter out selected person from family member options
  const filteredFamilySearchOptions = useMemo(() => {
    return familySearchOptions.filter(
      option => option?.id !== selectedPersonId
    );
  }, [familySearchOptions, selectedPersonId]);

  const { data: selectedPeopleData, refetch: selectedPeopleRefetch } =
    useGetPeopleByIdQuery(
      {
        id: selectedPersonId
      },
      {
        skip: !selectedPersonId
      }
    );

  useEffect(() => {
    if (selectedPersonId) {
      selectedPeopleRefetch();
    }
  }, [selectedPersonId]);

  const { data: peopleData, refetch: peopleRefetch } = useGetPeopleByIdQuery(
    {
      id: personId
    },
    {
      skip: !personId
    }
  );

  useEffect(() => {
    if (personId) {
      peopleRefetch();
    }
  }, [personId]);

  const isMotherExist = selectedPeopleData?.data?.isMother === 1;
  const isFatherExist = selectedPeopleData?.data?.isFather === 1;

  // Get existing relationship values from peopleData
  const existingRelationships = useMemo(() => {
    const relationships = [];
    if (peopleData?.data?.isFather) {
      relationships.push(peopleData.data.isFather);
    }
    if (peopleData?.data?.isMother) {
      relationships.push(peopleData.data.isMother);
    }
    return relationships;
  }, [peopleData?.data]);

  // Filter relationship options to exclude existing ones
  const filteredRelationshipOptions = useMemo(() => {
    return relationshipOptions.filter(
      option => !existingRelationships.includes(Number(option?.value))
    );
  }, [relationshipOptions, existingRelationships]);

  // Filter family member relation options based on selectedPeopleData
  const data = useMemo(() => {
    const existingRelations = [];
    if (selectedPeopleData?.data?.isFather) {
      existingRelations.push(selectedPeopleData?.data?.isFather);
    }
    if (selectedPeopleData?.data?.isMother) {
      existingRelations.push(selectedPeopleData?.data?.isMother);
    }
    return existingRelations;
  }, [selectedPeopleData?.data]);

  // Filter relationship options to exclude existing ones
  const filteredFamilyMemberRelationOptions = useMemo(() => {
    return relationshipOptions.filter(
      option => !data.includes(Number(option?.value))
    );
  }, [relationshipOptions, data]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
    register,
    setFocus
  } = useForm<RelativeFormData>({
    resolver: yupResolver(relativeFormSchema),
    defaultValues: {
      name: name || '',
      relationshipType: '',
      nickName: '',
      gender: '',
      birthday: '',
      birthPlace: '',
      familyMemberName: '',
      familyMemberRelation: '',
      diary: '',
      isPrivate: false,
      dadName: '',
      momName: '',
      dadId: '',
      momId: '',
      email: ''
    },
    context: { isNameSelected }
  } as any);

  const MAX_CHARS = 2000;
  const diaryValue = watch('diary') || '';
  const currentLength = diaryValue?.length;

  useEffect(() => {
    if (name) {
      setValue('name', name);
    }
  }, [name, setValue]);

  useEffect(() => {
    const firstError = Object.keys(errors)[0] as keyof RelativeFormData;
    if (firstError) {
      setFocus(firstError);
    }
  }, [errors, setFocus]);

  if (!isOpen) return null;

  const handleFormSubmit = async (data: RelativeFormData) => {
    setIsLoading(true);
    const combinedFamilyMembers = [
      ...(data.dadName
        ? [
            {
              id: data?.dadId || '',
              name: data?.dadName.trim(),
              relation: '1'
            }
          ]
        : []),

      ...(data.momName
        ? [
            {
              id: data?.momId || '',
              name: data?.momName.trim(),
              relation: '2'
            }
          ]
        : []),

      ...familyMembers.map(member => ({
        id: member?.id || '',
        name: member?.name.trim(),
        relation: member?.relation.trim()
      }))
    ];

    const dataObj = {
      createPeople: {
        type: 'family',
        relatedPeopleId: personId || '',
        peopleId: selectedPersonId || '',
        name: data.name || name,
        relationshipType: data.relationshipType,
        nickname: data.nickName,
        gender: data.gender,
        dateOfBirth: data.birthday ? data.birthday : undefined,
        birthPlace: data.birthPlace,
        familyMembers: combinedFamilyMembers,
        diary: data.diary,
        isPrivate: isPrivate,
        email: data.email
      }
    };

    try {
      const res: any = await AddFamily(dataObj);

      if (res?.data) {
        const newPeopleId = res.data.data.id;

        // If profile picture is selected, upload it
        if (profilePictureFile && newPeopleId) {
          try {
            const idToken = token || '';
            if (!idToken) {
              throw new Error('Authentication token not found');
            }

            // Upload to S3
            const imageUrl = await uploadToS3(
              profilePictureFile,
              idToken,
              userDetails?.userId || '',
              'profile',
              '',
              {
                userId: userDetails?.userId
              }
            );

            // Update people with profile image
            const updateDataObj = {
              id: newPeopleId,
              updatePeople: {
                profileImage: imageUrl
              }
            };

            await UpdatePeople(updateDataObj);
          } catch (uploadError: any) {
            console.error('Profile picture upload error:', uploadError);
            // Don't fail the whole operation, just show a warning
            dispatch(
              setAlertDetails({
                alert: {
                  type: 'warning',
                  message: t('Relative added but profile picture upload failed')
                },
                scope: 'people'
              })
            );
          }
        }

        dispatch(
          setAlertDetails({
            alert: {
              type: 'success',
              message: 'Relative added successfully'
            },
            scope: 'people'
          })
        );
        setIsLoading(false);
        setFamilyMembers([]);
        setCurrentFamilyMemberInput({ id: '', name: '', relation: '' });
        setShowFamilyMember(false);
        setProfilePictureFile(null);
        setProfilePicturePreview('');
        peopleFiltersHandler();
        reset();
        onClose();
      } else {
        setIsLoading(false);
        setShowAlert(true);
        setAlertData({
          type: 'error',
          message: res?.error?.data?.data
        });
      }
    } catch (err: any) {
      setIsLoading(false);
      setShowAlert(true);
      setAlertData({
        type: 'error',
        message: err?.message
      });
    }
  };

  const handleCancel = () => {
    reset();
    setFamilyMembers([]);
    setCurrentFamilyMemberInput({ id: '', name: '', relation: '' });
    setShowFamilyMember(false);
    setSelectedPersonId('');
    setHasNameInteraction(false);
    setProfilePictureFile(null);
    setProfilePicturePreview('');
    if (profilePicturePreview) {
      URL.revokeObjectURL(profilePicturePreview);
    }
    onClose();
  };

  const handleAddFamilyMember = () => {
    if (
      !currentFamilyMemberInput.name?.trim() ||
      !currentFamilyMemberInput.relation?.trim()
    )
      return;

    setFamilyMembers(prev => [
      ...prev,
      {
        id: currentFamilyMemberInput.id || '',
        name: currentFamilyMemberInput.name.trim(),
        relation: currentFamilyMemberInput.relation.trim()
      }
    ]);

    setCurrentFamilyMemberInput({ id: '', name: '', relation: '' });
    setShowFamilyMember(false);
  };

  const handleFamilyMemberInputChange = (
    field: 'name' | 'relation',
    value: any
  ) => {
    if (field === 'name') {
      setCurrentFamilyMemberInput(prev => ({
        ...prev,
        id: value?.id || '',
        name: value?.name || ''
      }));
    } else {
      setCurrentFamilyMemberInput(prev => ({
        ...prev,
        relation: value
      }));
    }
  };

  const handleRemoveFamilyMember = (index: number) => {
    setFamilyMembers(familyMembers.filter((_, i) => i !== index));

    // If the removed member is currently being edited, close edit mode
    if (editingMember?.index === index) {
      setEditingMember(null);
    }
  };

  const handleEditFamilyMember = (index: number) => {
    const member = familyMembers[index];
    setEditingMember({
      index,
      id: member.id || '',
      name: member.name,
      relation: member.relation
    });

    // hide add input while editing
    setShowFamilyMember(false);
  };

  const handleEditChange = (field: 'name' | 'relation', value: any) => {
    if (!editingMember) return;

    if (field === 'name') {
      setEditingMember({
        ...editingMember,
        id: value?.id || '',
        name: value?.name || ''
      });
    } else {
      setEditingMember({
        ...editingMember,
        relation: value
      });
    }
  };

  const handleUpdateFamilyMember = () => {
    if (
      !editingMember ||
      !editingMember.name?.trim() ||
      !editingMember.relation?.trim()
    )
      return;

    setFamilyMembers(prev => {
      const updated = [...prev];

      updated[editingMember.index] = {
        id: editingMember.id || '',
        name: editingMember.name.trim(),
        relation: editingMember.relation.trim()
      };

      return updated;
    });

    setEditingMember(null);
  };

  const handleCancelEdit = () => {
    setEditingMember(null);
  };

  const searchFamilyMembers = (value: string) => {
    setSearchText(value);
  };

  const getRelationLabel = (value: string) => {
    const option = relationshipTypeOptions?.data?.rows.find(
      (opt: any) => opt?.id === Number(value)
    );
    return option?.name || value;
  };

  const hasUnsavedFamilyMember =
    showFamilyMember &&
    (currentFamilyMemberInput.name.trim() !== '' ||
      currentFamilyMemberInput.relation.trim() !== '');

  const hasUnsavedEdit = !!editingMember;

  const shouldBlockSubmit = hasUnsavedFamilyMember || hasUnsavedEdit;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto p-6 relative animate-fadeIn">
        {/* Close Button */}
        <Button
          btnStyle="link"
          className="absolute right-4 top-4"
          onClick={handleCancel}>
          <SVGAssets.CloseIconGray className="w-4 h-4" />
        </Button>
        {showAlert && (
          <div>
            <Alert
              onClose={() => {
                setShowAlert(false);
              }}
              {...alertData}
              autoHide={true}
            />
          </div>
        )}

        {/* Header */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-800">
            {t('peopleDetail.addRelative')}
          </h2>
          <p className="!text-xs text-gray-500 mt-1">
            {t('peopleDetail.addToFamilyDescription')}
          </p>
        </div>

        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
          {/* Name */}
          <label className="block text-sm font-semibold text-gray-700 mb-1">
            {t('peopleDetail.name')}
            <span className="text-red-500 ml-1">*</span>
          </label>

          <SearchableSelectInput
            name="name"
            placeholder={t('peopleDetail.enterName')}
            control={control}
            isWhiteTheme
            options={familySearchOptions}
            onSearch={(value: string) => {
              setSearchText(value);
              if (value) {
                setHasNameInteraction(true);
              }
            }}
            onChange={({ id, name }) => {
              setValue('name', name, { shouldValidate: true });
              setValue('personId', id || '');
              setIsNameSelected(!!id);
              setSelectedPersonId(id || '');
              if (name) {
                setHasNameInteraction(true);
              }
            }}
          />

          {errors.name && (
            <p className="text-red-500 text-xs mt-1">{errors.name.message}</p>
          )}

          {!selectedPersonId &&
            !isNameSelected &&
            hasNameInteraction &&
            watch('name') && (
              <>
                <div
                  className="flex w-full items-center cursor-pointer"
                  onClick={() => setShowUploadModal(true)}>
                  <SVGAssets.AddRoundIcon className="w-4 h-4" />
                  <span className="text-sm text-[#49712B] ml-2 font-semibold">
                    {t('addProfilePic')}
                  </span>
                </div>
                {profilePicturePreview && (
                  <div className="flex items-center gap-3 mt-2">
                    <img
                      src={profilePicturePreview}
                      alt="Profile preview"
                      className="w-16 h-16 rounded-full object-cover"
                    />
                    <Button
                      btnStyle="link"
                      type="button"
                      onClick={() => {
                        setProfilePictureFile(null);
                        if (profilePicturePreview) {
                          URL.revokeObjectURL(profilePicturePreview);
                        }
                        setProfilePicturePreview('');
                      }}
                      className="text-red-500 text-sm">
                      {t('remove')}
                    </Button>
                  </div>
                )}
              </>
            )}
          {/* Relationship Type */}
          <div
            className={isNameSelected ? 'opacity-50 pointer-events-none' : ''}>
            <label className="block text-sm font-semibold text-[#353535] mb-1">
              {t('peopleDetail.relationshipType')}
              <span className="text-red-500 ml-1">*</span>
            </label>

            <Select
              name="relationshipTypeOfPerson"
              options={[
                { value: '', label: 'Select Relation' },
                ...filteredRelationshipOptions
              ]}
              value={watch('relationshipType')}
              onChange={e =>
                setValue('relationshipType', e.target.value, {
                  shouldValidate: true
                })
              }
              selectClassName="friend-select mb-3"
              disabled={isNameSelected}
            />

            {errors.relationshipType && (
              <p className="text-red-500 text-xs mt-1">
                {errors.relationshipType.message}
              </p>
            )}

            <Input
              name="email"
              label={t('email')}
              placeholder={t('enterEmail')}
              control={control}
              isWhiteTheme
              type="text"
              disabled={isNameSelected}
            />
          </div>

          {/* Nick Name & Gender */}
          <div
            className={`grid grid-cols-2 gap-4 ${isNameSelected ? 'opacity-50 pointer-events-none' : ''}`}>
            <Input
              name="nickName"
              label={t('peopleDetail.nickName')}
              placeholder={t('peopleDetail.enterNickName')}
              control={control}
              isWhiteTheme
              type="text"
              disabled={isNameSelected}
            />

            <div className="mt-2">
              <label className="block text-sm font-medium text-[#353535] mb-1">
                {t('peopleDetail.gender')}
                <span className="text-red-500 ml-1">*</span>
              </label>

              <Select
                name="gender"
                options={[
                  {
                    label: t('peopleDetail.chooseGender'),
                    value: ''
                  },
                  {
                    label: t('peopleDetail.male'),
                    value: 'male'
                  },
                  {
                    label: t('peopleDetail.female'),
                    value: 'female'
                  },
                  {
                    label: t('peopleDetail.other'),
                    value: 'other'
                  }
                ]}
                value={watch('gender')}
                onChange={e =>
                  setValue('gender', e.target.value, { shouldValidate: true })
                }
                selectClassName="friend-select"
                disabled={isNameSelected}
              />

              {errors.gender && (
                <p className="text-xs text-red-500 mt-1">
                  {errors.gender.message}
                </p>
              )}
            </div>
          </div>

          {/* Birthday & Birth Place */}
          <div
            className={`grid grid-cols-2 gap-4 ${isNameSelected ? 'opacity-50 pointer-events-none' : ''}`}>
            <Input
              name="birthday"
              label={t('peopleDetail.birthday')}
              control={control}
              type="date"
              isWhiteTheme
              max={new Date().toISOString().split('T')[0]}
              disabled={isNameSelected}
            />

            <Input
              name="birthPlace"
              label={t('peopleDetail.birthPlace')}
              placeholder={t('peopleDetail.enterBirthPlace')}
              control={control}
              type="text"
              isWhiteTheme
              disabled={isNameSelected}
            />
          </div>
          {!isFatherExist && !isMotherExist && (
            <div
              className={`grid grid-cols-2 gap-4 ${isNameSelected ? 'opacity-50 pointer-events-none' : ''}`}>
              <div>
                <SearchableSelectInput
                  name="dadName"
                  label={t('peopleDetail.dadName')}
                  placeholder={t('peopleDetail.enterDadName')}
                  control={control}
                  isWhiteTheme
                  options={filteredFamilySearchOptions}
                  onSearch={searchFamilyMembers}
                  onChange={({ id, name }) => {
                    setValue('dadName', name, { shouldValidate: true });
                    setValue('dadId', id || '');
                  }}
                />
              </div>

              <div>
                <SearchableSelectInput
                  name="momName"
                  label={t('peopleDetail.momName')}
                  placeholder={t('peopleDetail.enterMomName')}
                  control={control}
                  isWhiteTheme
                  options={filteredFamilySearchOptions}
                  onSearch={searchFamilyMembers}
                  onChange={({ id, name }) => {
                    setValue('momName', name, { shouldValidate: true });
                    setValue('momId', id || '');
                  }}
                />
              </div>
            </div>
          )}

          {/* Add Family Member Button */}
          <div
            className="flex w-full items-center cursor-pointer"
            onClick={() => setShowFamilyMember(true)}>
            <SVGAssets.AddRoundIcon className="w-4 h-4" />
            <span className="text-sm text-[#49712B] ml-2 font-semibold">
              {t('peopleDetail.addFamilyMember')}
            </span>
          </div>

          {/* SAVED FAMILY MEMBERS */}
          <div className="space-y-3">
            {familyMembers.map((member, index) => (
              <div key={index} className="space-y-2">
                {editingMember?.index === index ? (
                  /* EDIT MODE */
                  <div className="grid grid-cols-[1fr_1fr_auto] gap-4 items-center">
                    <SearchableSelectInput
                      label={t('peopleDetail.memberName')}
                      isWhiteTheme
                      value={editingMember.name}
                      options={filteredFamilySearchOptions}
                      onSearch={searchFamilyMembers}
                      onChange={({ id, name }) =>
                        handleEditChange('name', { id, name })
                      }
                      isSelectOnly={true}
                    />

                    <div>
                      <label className="block text-sm font-medium text-[#353535] mb-1">
                        {t('peopleDetail.relation')}
                      </label>
                      <Select
                        name="family-member-relation"
                        options={[
                          { value: '', label: 'Select Relation' },
                          ...filteredFamilyMemberRelationOptions
                        ]}
                        value={editingMember.relation}
                        onChange={e =>
                          handleEditChange('relation', e.target.value)
                        }
                        selectClassName="friend-select"
                      />
                    </div>

                    <div className="flex items-center gap-1 pt-4">
                      <Button
                        btnStyle="link"
                        type="button"
                        onClick={handleUpdateFamilyMember}>
                        <SVGAssets.GreenTick className="w-4 h-4" />
                      </Button>

                      <Button
                        btnStyle="link"
                        type="button"
                        onClick={handleCancelEdit}>
                        <SVGAssets.CloseIcon className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  /* VIEW MODE */
                  <div className="flex items-center justify-between bg-white px-4 py-3 rounded-lg">
                    {/* Left content */}
                    <div className="flex flex-1 gap-16">
                      <div>
                        <p className="text-[11px] text-gray-400">
                          {t('peopleDetail.memberName')}
                        </p>
                        <p className="text-sm font-semibold text-gray-900">
                          {member?.name}
                        </p>
                      </div>

                      <div>
                        <p className="text-[11px] text-gray-400">
                          {t('peopleDetail.relation')}
                        </p>
                        <p className="text-sm font-semibold text-gray-900">
                          {getRelationLabel(member?.relation)}
                        </p>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-3">
                      <Button
                        btnStyle="link"
                        type="button"
                        onClick={() => handleEditFamilyMember(index)}>
                        <SVGAssets.PencilIcon className="w-4 h-4" />
                      </Button>

                      <Button
                        btnStyle="link"
                        type="button"
                        onClick={() => handleRemoveFamilyMember(index)}>
                        <SVGAssets.TrashIcon className="w-4 h-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {/* ADD INPUT – controlled by showFamilyMember */}
            {showFamilyMember && (
              <div className="grid grid-cols-[1fr_1fr_auto] gap-4 items-center">
                <SearchableSelectInput
                  label={t('peopleDetail.memberName')}
                  placeholder={t('fullName')}
                  isWhiteTheme
                  value={currentFamilyMemberInput.name}
                  options={filteredFamilySearchOptions}
                  onSearch={searchFamilyMembers}
                  onChange={({ id, name }) =>
                    handleFamilyMemberInputChange('name', { id, name })
                  }
                  isSelectOnly={true}
                />

                <div>
                  <label className="block text-sm font-medium text-[#353535] mb-1">
                    {t('peopleDetail.relation')}
                  </label>
                  <Select
                    name="family-member-relation"
                    options={[
                      { value: '', label: 'Select Relation' },
                      ...filteredFamilyMemberRelationOptions
                    ]}
                    value={currentFamilyMemberInput.relation}
                    onChange={e =>
                      handleFamilyMemberInputChange('relation', e.target.value)
                    }
                    selectClassName="friend-select"
                  />
                </div>

                <div className="flex items-center gap-1 pt-4">
                  <Button
                    btnStyle="link"
                    type="button"
                    onClick={handleAddFamilyMember}
                    className="px-1 h-fit">
                    <SVGAssets.GreenTick className="w-4 h-4" />
                  </Button>

                  <Button
                    btnStyle="link"
                    type="button"
                    onClick={() => {
                      setShowFamilyMember(false);
                      setCurrentFamilyMemberInput({
                        id: '',
                        name: '',
                        relation: ''
                      });
                    }}
                    className="px-1 h-fit">
                    <SVGAssets.CloseIcon className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Diary */}
          <div
            className={isNameSelected ? 'opacity-50 pointer-events-none' : ''}>
            <div className="flex justify-between w-full mb-2">
              <label className="block text-sm font-semibold text-[#353535]">
                {t('peopleDetail.diary')}
              </label>

              {/* Private Toggle */}
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-[#49712B] mr-2">
                  {t('peopleDetail.private')}
                </span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isPrivate}
                    onChange={e => setIsPrivate(e.target.checked)}
                    className="sr-only peer"
                    disabled={isNameSelected}
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-[#7F9D48] after:absolute after:top-[2px] after:left-[2px] after:h-5 after:w-5 after:bg-white after:rounded-full after:transition-all peer-checked:after:translate-x-full" />
                </label>
              </div>
            </div>

            <div>
              <textarea
                {...register('diary')}
                maxLength={MAX_CHARS}
                rows={3}
                placeholder={t('peopleDetail.desc')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm
               focus:outline-none focus:ring-1 focus:ring-[#8d8d8d] resize-none"
                disabled={isNameSelected}
              />
              <div className=" text-xs text-gray-400 flex justify-end">
                {currentLength}/{MAX_CHARS}
              </div>
            </div>

            {errors.diary && (
              <p className="text-red-500 text-xs mt-1">
                {errors.diary.message}
              </p>
            )}
          </div>
          {shouldBlockSubmit && (
            <div className="text-xs text-orange-600 bg-orange-50 border border-orange-200 rounded-md px-3 py-2">
              {t('peopleDetail.familyMemberChangesWarning')}
            </div>
          )}
          {/* Action Buttons */}
          <div className="flex justify-center items-center gap-3 pt-4">
            <Button
              btnStyle="link"
              className="px-6 py-2 !bg-[#F9FEE9] border-[var(--na-primary-color)] text-[var(--na-primary-color)] font-medium rounded-md hover:bg-[#F9FEE9] text-sm"
              onClick={handleCancel}>
              {t('cancel')}
            </Button>
            <Button
              btnStyle="primary"
              type="submit"
              isDisabled={shouldBlockSubmit}
              className="px-6 rounded-lg py-2">
              {t('peopleDetail.addFamily')}
            </Button>
          </div>
        </form>
      </div>
      {showUploadModal && (
        <UploadPhotoModal
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          userId={userDetails?.userId || ''}
          isPeopleProfilePic={false}
          skipUpload={true}
          onFileSelect={(file: File) => {
            setProfilePictureFile(file);
            const previewUrl = URL.createObjectURL(file);
            setProfilePicturePreview(previewUrl);
            setShowUploadModal(false);
          }}
        />
      )}
    </div>
  );
};
